import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Camera, QrCode, Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Scanner = () => {
  const [scanning, setScanning] = useState(false);
  const [manualId, setManualId] = useState('');
  const [searching, setSearching] = useState(false);
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      // Cleanup scanner on unmount
      if (html5QrcodeRef.current && html5QrcodeRef.current.isScanning) {
        html5QrcodeRef.current.stop().catch(console.error);
      }
    };
  }, []);

  const startScanner = async () => {
    try {
      if (!html5QrcodeRef.current) {
        html5QrcodeRef.current = new Html5Qrcode("qr-reader");
      }

      setScanning(true);
      
      await html5QrcodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          handleScan(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors (no QR found in frame)
        }
      );
    } catch (err) {
      console.error("Scanner error:", err);
      toast.error("Could not access camera. Please check permissions.");
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current && html5QrcodeRef.current.isScanning) {
      await html5QrcodeRef.current.stop();
    }
    setScanning(false);
  };

  const handleScan = async (data) => {
    await stopScanner();
    
    // Check if it's a URL with item, container, or wardrobe ID
    try {
      const url = new URL(data);
      const pathParts = url.pathname.split('/');
      const itemIndex = pathParts.indexOf('item');
      const containerIndex = pathParts.indexOf('container');
      const wardrobeIndex = pathParts.indexOf('wardrobe');
      if (itemIndex !== -1 && pathParts[itemIndex + 1]) {
        navigate(`/item/${pathParts[itemIndex + 1]}`);
        return;
      }
      if (containerIndex !== -1 && pathParts[containerIndex + 1]) {
        navigate(`/container/${pathParts[containerIndex + 1]}`);
        return;
      }
      if (wardrobeIndex !== -1 && pathParts[wardrobeIndex + 1]) {
        navigate(`/wardrobe/${pathParts[wardrobeIndex + 1]}`);
        return;
      }
    } catch (e) {}

    try {
      const parsed = JSON.parse(data);
      if (parsed.id) {
        navigate(`/item/${parsed.id}`);
        return;
      }
    } catch (e) {}

    if (data.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      navigate(`/item/${data}`);
      return;
    }

    toast.error("Invalid QR code format");
  };

  const handleManualSearch = async () => {
    if (!manualId.trim()) return;
    setSearching(true);
    const query = manualId.trim();
    
    try {
      const itemResp = await axios.get(`${API_URL}/api/items/lookup/${encodeURIComponent(query)}`);
      if (itemResp.data?.id) { navigate(`/item/${itemResp.data.id}`); return; }
    } catch (error) {}
    
    try {
      const containerResp = await axios.get(`${API_URL}/api/containers/lookup/${encodeURIComponent(query)}`);
      if (containerResp.data?.id) { navigate(`/container/${containerResp.data.id}`); return; }
    } catch (error) {}
    
    try {
      const wardrobeResp = await axios.get(`${API_URL}/api/wardrobes/lookup/${encodeURIComponent(query)}`);
      if (wardrobeResp.data?.id) { navigate(`/wardrobe/${wardrobeResp.data.id}`); return; }
    } catch (error) {
      toast.error(`"${query}" not found`);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="scanner-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">QR Scanner</h1>
        <p className="text-muted-foreground mt-1">Scan a QR code to view item details</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Scanner Card */}
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Camera Scanner</CardTitle>
            </div>
            <CardDescription>Use your camera to scan a QR code on the equipment</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              id="qr-reader" 
              ref={scannerRef}
              className={`w-full aspect-square rounded-xl overflow-hidden bg-muted ${!scanning ? 'hidden' : ''}`}
            />
            
            {!scanning && (
              <div className="w-full aspect-square rounded-xl bg-muted flex flex-col items-center justify-center gap-4">
                <div className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <QrCode className="w-12 h-12 text-primary" />
                </div>
                <p className="text-muted-foreground text-center">
                  Click the button below to start scanning
                </p>
              </div>
            )}
            
            {scanning ? (
              <Button onClick={stopScanner} variant="outline" className="w-full" data-testid="stop-scanner-btn">
                <X className="w-4 h-4 mr-2" />
                Stop Scanner
              </Button>
            ) : (
              <Button onClick={startScanner} className="w-full" data-testid="start-scanner-btn">
                <Camera className="w-4 h-4 mr-2" />
                Start Scanner
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Manual Entry Card */}
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Manual Entry</CardTitle>
            </div>
            <CardDescription>Enter an item number directly if you can't scan</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Input
                data-testid="manual-item-id"
                placeholder="Item # (FIX-918), Container (PROP-BIN-01), Wardrobe (WARD-01)"
                value={manualId}
                onChange={(e) => setManualId(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                className="font-mono text-sm"
              />
            </div>
            <Button 
              onClick={handleManualSearch} 
              className="w-full" 
              disabled={!manualId.trim() || searching} 
              data-testid="search-item-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              {searching ? 'Searching...' : 'Find Item'}
            </Button>

            <div className="pt-4 border-t border-border">
              <h4 className="font-medium mb-2">How to use:</h4>
              <ol className="text-sm text-muted-foreground space-y-2">
                <li>1. Click "Start Scanner" to activate your camera</li>
                <li>2. Point the camera at a QR code on the equipment</li>
                <li>3. The item details will load automatically</li>
                <li>4. Or type the Item # (like FIX-918) above</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Scanner;
